import React from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { QuotationDetailsViewProps } from "models/propTypes/quotationPropTypes";
//#endregion
//#region components
import {
  ViewPanel,
  FormValidity,
  PageLayouts,
  EntityTitle,
  useMountedState,
  Skeletons,
  FileViewerContainer,
  GenericCommentsView,
} from "components/common";
import QuotationDetailsFormContainer from "./forms/detailsFormContainer";
import QuotationFooter from "./footer";
import QuotationBanner from "./quotationBanner";
import QuotationStateFlow from "./stateFlow";
import { useQuotesService, useImageService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const QuotationDetailsView: React.FC<QuotationDetailsViewProps> = (props) => {
  const imageService = useImageService();
  const quotesService = useQuotesService();

  const { quotation, reloadQuotation } = props;
  const { getMounted } = useMountedState();
  const formRef = React.useRef<FormHandles>(null);

  const onUpdateInvoice = () => {
    return (formRef.current && formRef.current.submit()) || new Promise<void>((resolve) => resolve());
  };

  const [attachments, setAttachments] = React.useState<ImageMeta[]>([]);

  React.useEffect(() => {
    if (quotation) {
      Promise.all([
        imageService.getImagesMeta(quotation.serialNumber, WintSuperkollAPIModel.ImageType.Quotation),
        quotesService.getQuotationPDFPreview(quotation.serialNumber),
      ]).then(([imageMeta, quotationHtml]) => {
        if (getMounted()) {
          const quotationHtmlImg: ImageMeta[] = [
            {
              id: "quotationHtml",
              contentType: "html",
              numberOfPages: 1,
              data: quotationHtml.data as unknown as string,
            },
          ];

          const attachments =
            imageMeta.numberOfAttachments > 0 && imageMeta.attachmentsMeta
              ? imageMeta.attachmentsMeta.map(
                  (meta): ImageMeta => ({
                    id: meta.attachmentId ?? "",
                    contentType: meta.contentType,
                    numberOfPages: meta.numberOfPages,
                  })
                )
              : [];

          setAttachments(quotationHtmlImg.concat(attachments));
        }
      });
    }
  }, [quotation?.serialNumber, getMounted]);

  const [displayType, setDisplayType] = React.useState<StyleEnums.DisplayType>(StyleEnums.DisplayType.Info);

  React.useEffect(() => {
    const updatedDisplayType =
      quotation && quotesService.checkQuotationExpired(quotation)
        ? StyleEnums.DisplayType.Danger
        : quotesService.getUIDisplayState(quotation);
    if (updatedDisplayType !== displayType) setDisplayType(updatedDisplayType);
  }, [quotation?.quotationState]);

  return quotation ? (
    <FormValidity
      render={(isValid, onValidityChange) => (
        <PageLayouts.EntityViewLayout
          displayType={displayType}
          upperLeftComponent={
            <EntityTitle
              status={quotesService.getDisplayState(quotation)}
              subTitle={LocalizationHelperInstance.getFormattedMessage(
                quotation.quotationState === WintSuperkollAPIModel.QuotationApiState.Won ||
                  quotation.quotationState === WintSuperkollAPIModel.QuotationApiState.InvoiceCreated
                  ? TranslationKeys.Quotation.CustomerInvoiceTo
                  : TranslationKeys.Common.Quotation
              )}
              title={quotation.customerName ?? ""}
            />
          }
          upperRightComponent={
            quotation ? (
              <QuotationStateFlow serialNumber={quotation.serialNumber} />
            ) : (
              <Skeletons.StateFlowSkeleton key="steps-skeleton" stepCount={4} />
            )
          }
          lowerLeftComponent={
            <FileViewerContainer
              meta={attachments}
              parentId={quotation.serialNumber}
              type={WintSuperkollAPIModel.ImageType.Quotation}
              preventDownload
            />
          }
          footer={
            <QuotationFooter
              isValid={isValid}
              quotation={quotation}
              createOrUpdateQuotation={onUpdateInvoice}
              reloadHandler={() => reloadQuotation && reloadQuotation()}
              markAsDoneHandler={() => reloadQuotation && reloadQuotation()}
            />
          }
        >
          <>
            {quotation.quotationState !== WintSuperkollAPIModel.QuotationApiState.Cancelled && (
              <QuotationBanner quotation={quotation} displayType={displayType} reloadHandler={reloadQuotation} />
            )}

            <ViewPanel
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Details)}
              icon={IconDefinitionConstants.WEdit}
              classes={{ content: cssExports.detailsFormContainer }}
            >
              <QuotationDetailsFormContainer
                ref={formRef}
                quotation={quotation}
                onFormValidationChange={onValidityChange}
                loadQuotation={reloadQuotation}
              />
            </ViewPanel>
            <GenericCommentsView
              parentId={quotation.serialNumber}
              type={WintSuperkollAPIModel.GenericCommentType.Quotation}
            />
          </>
        </PageLayouts.EntityViewLayout>
      )}
    />
  ) : (
    <></>
  );
};
export default QuotationDetailsView;
